import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { VideoJsPlayer } from 'video.js';

export function useVideoPlayerState(player: VideoJsPlayer) {
  const subscribePausePlay = useCallback(
    (notify: () => void) => {
      player.on('pause', notify);
      player.on('play', notify);
      player.on('ended', notify);

      return () => {
        player.off('pause', notify);
        player.off('play', notify);
        player.off('ended', notify);
      };
    },
    [player]
  );

  const subscribeVolume = useCallback(
    (notify: () => void) => {
      player.on('volumechange', notify);
      return () => player.off('volumechange', notify);
    },
    [player]
  );

  const subscribeDuration = useCallback(
    (notify: () => void) => {
      player.on('loadedmetadata', notify);
      player.on('durationchange', notify);

      return () => {
        player.off('loadedmetadata', notify);
        player.off('durationchange', notify);
      };
    },
    [player]
  );

  const subscribeFullscreen = useCallback(
    (notify: () => void) => {
      player.on('fullscreenchange', notify);
      return () => player.off('fullscreenchange', notify);
    },
    [player]
  );

  const isPaused = useSyncExternalStore(subscribePausePlay, () =>
    player.paused()
  );

  const volume = useSyncExternalStore(subscribeVolume, () => player.volume());

  const duration = useSyncExternalStore(
    subscribeDuration,
    () => player.duration() || 0
  );

  const isExpand = useSyncExternalStore(subscribeFullscreen, () =>
    player.isFullscreen()
  );

  const [currentTime, setCurrentTime] = useState(() => player.currentTime());

  const [buffered, setBuffered] = useState(() => {
    try {
      return player.bufferedEnd();
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    let rafId: number | null = null;

    const updateTime = () => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        setCurrentTime(player.currentTime());
      });
    };

    const updateBuffered = () => {
      setBuffered(player.bufferedEnd());
    };

    player.on('timeupdate', updateTime);
    player.on('seeking', updateTime);
    player.on('seeked', updateTime);
    player.on('ended', updateTime);

    player.on('progress', updateBuffered);

    return () => {
      player.off('timeupdate', updateTime);
      player.off('seeking', updateTime);
      player.off('seeked', updateTime);
      player.off('ended', updateTime);

      player.off('progress', updateBuffered);

      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [player]);

  return {
    isPaused,
    volume,
    currentTime,
    duration,
    isExpand,
    buffered
  };
}
